import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';

interface DatabaseDebuggerProps {
  className?: string;
}

const DatabaseDebugger: React.FC<DatabaseDebuggerProps> = ({ className }) => {
  const [results, setResults] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runDatabaseChecks = async () => {
    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const checks = await Promise.allSettled([
        // Check 1: Test user_profiles table access
        supabase
          .from('user_profiles')
          .select('id, user_id, username, created_at')
          .limit(5)
          .then(({ data, error }) => ({
            name: 'user_profiles_access',
            data: {
              accessible: !error,
              recordCount: data?.length || 0,
              sampleRecords: data?.slice(0, 2) || []
            },
            error: error?.message
          })),

        // Check 2: Test user_profiles insert permissions
        supabase
          .from('user_profiles')
          .select('id')
          .limit(0)
          .then(({ error }) => ({
            name: 'user_profiles_permissions',
            data: { 
              canRead: !error,
              message: error ? 'Read access denied' : 'Read access OK'
            },
            error: error?.message
          })),

        // Check 3: Current auth status
        supabase.auth.getUser().then(({ data, error }) => ({
          name: 'current_auth_status',
          data: {
            isAuthenticated: !!data.user,
            userId: data.user?.id,
            email: data.user?.email,
            confirmed: !!data.user?.email_confirmed_at
          },
          error: error?.message
        })),

        // Check 4: Test auth session
        supabase.auth.getSession().then(({ data, error }) => ({
          name: 'auth_session',
          data: {
            hasSession: !!data.session,
            sessionValid: !!data.session?.access_token,
            expiresAt: data.session?.expires_at
          },
          error: error?.message
        })),

        // Check 5: Test basic database connectivity
        supabase
          .from('displays')
          .select('id')
          .limit(1)
          .then(({ data, error }) => ({
            name: 'database_connectivity',
            data: {
              connected: !error,
              displaysAccessible: !!data
            },
            error: error?.message
          })),
      ]);

      const checkResults = checks.map((result, index) => {
        if (result.status === 'fulfilled') {
          return result.value;
        } else {
          return {
            name: `check_${index}`,
            data: null,
            error: result.reason?.message || 'Unknown error'
          };
        }
      });

      setResults(checkResults);
    } catch (err: any) {
      setError(`Failed to run database checks: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const testSignupFlow = async () => {
    setLoading(true);
    setError(null);

    try {
      // Try to test the signup flow with a clearly fake test email
      const testEmail = `debug-test-${Date.now()}@example.invalid`;
      const testPassword = 'TestPassword123!';

      console.log('🧪 Testing signup flow with:', testEmail);

      const { data, error } = await supabase.auth.signUp({
        email: testEmail,
        password: testPassword,
        options: {
          data: {
            full_name: 'Debug Test User'
          }
        }
      });

      // Check if a profile was created (if signup succeeded)
      let profileCreated = false;
      let profileError = null;
      
      if (data?.user?.id && !error) {
        try {
          const { data: profileData, error: profileErr } = await supabase
            .from('user_profiles')
            .select('id, username')
            .eq('user_id', data.user.id)
            .limit(1);
          
          profileCreated = !profileErr && profileData && profileData.length > 0;
          profileError = profileErr?.message;
        } catch (profileCheckError) {
          profileError = 'Failed to check profile creation';
        }
      }

      setResults([{
        name: 'signup_flow_test',
        data: {
          step1_auth_success: !error,
          step2_user_created: !!data?.user,
          step3_user_id: data?.user?.id,
          step4_session_created: !!data?.session,
          step5_confirmation_sent: data?.user && !data?.user?.email_confirmed_at,
          step6_profile_created: profileCreated,
          profile_error: profileError,
          full_error_details: error
        },
        error: error?.message
      }]);

      if (data?.user?.id && !error) {
        console.log('⚠️ Test user created but not cleaned up automatically. Manual cleanup may be needed.');
      }

    } catch (err: any) {
      setError(`Signup test failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (check: any) => {
    if (check.error) {
      return <Badge variant="destructive">Error</Badge>;
    }
    if (check.data !== null && check.data !== undefined) {
      return <Badge variant="default">OK</Badge>;
    }
    return <Badge variant="secondary">Unknown</Badge>;
  };

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>Database Debugger</CardTitle>
        <CardDescription>
          Debug database state and test signup flow
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Button 
            onClick={runDatabaseChecks} 
            disabled={loading}
            variant="outline"
          >
            {loading ? 'Checking...' : 'Check Database State'}
          </Button>
          
          <Button 
            onClick={testSignupFlow} 
            disabled={loading}
            variant="outline"
          >
            {loading ? 'Testing...' : 'Test Signup Flow'}
          </Button>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {results && (
          <div className="space-y-3">
            <h3 className="font-medium">Results:</h3>
            {results.map((check: any, index: number) => (
              <div key={index} className="p-3 border rounded-md">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-sm">{check.name}</span>
                  {getStatusBadge(check)}
                </div>
                
                {check.error && (
                  <div className="text-sm text-red-600 mb-2">
                    <strong>Error:</strong> {check.error}
                  </div>
                )}
                
                {check.data && (
                  <pre className="text-xs bg-gray-50 p-2 rounded overflow-auto max-h-32">
                    {JSON.stringify(check.data, null, 2)}
                  </pre>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="text-xs text-muted-foreground">
          This tool helps diagnose database configuration issues.
          Use in development only.
        </div>
      </CardContent>
    </Card>
  );
};

export default DatabaseDebugger;